import type { Reaction } from "./channel";

/**
 * ═══ LE MUR DE RÉACTIONS ═══
 *
 * Les bulles qui montent sur l'écran de salle quand le public appuie sur un
 * bouton de réaction. C'est la contrepartie visible du geste : sans mur, une
 * réaction part dans le vide et personne, ni celui qui l'envoie ni la salle, ne
 * voit qu'il s'est passé quelque chose.
 *
 * ⚠️ IL PASSE PAR-DESSUS, IL NE REMPLACE RIEN. La V1 en fait un écran à part
 * (`/live/emotions`), donc un état de plus à piloter et un moment où la salle
 * montre des bulles à la place du sondage en cours. Ici c'est une couche : le
 * module reste à l'écran, les réactions traversent.
 *
 * ⚠️ ET IL NE PREND AUCUN CLIC. Un calque en position absolue au-dessus de tout
 * intercepterait les gestes de la page qu'il recouvre ; `pointer-events: none` le
 * rend transparent à la main comme à la souris.
 *
 * ⚠️ CE COMPOSANT MANQUAIT, et son absence était invisible : `useLive` accumulait
 * les réactions reçues (soixante au plus), l'API les diffusait à toutes les
 * connexions de l'événement, chaque arrivée provoquait un rendu de tout l'arbre du
 * direct, et rien ne les affichait. Du travail à chaque réaction, pour rien.
 */
export function Wall({ reactions }: { reactions: Reaction[] }) {
  if (!reactions.length) return null;

  return (
    <div className="wall" aria-hidden="true">
      {reactions.map((reaction, index) => (
        <img
          key={reaction.id}
          className="wall__r"
          src={reaction.icon}
          alt=""
          /**
           * La position horizontale est tirée de l'identifiant, pas d'un tirage au
           * sort à chaque rendu : sinon la bulle sauterait d'un bord à l'autre à
           * chaque nouvelle réaction, puisque le rendu se rejoue en entier.
           */
          style={{ left: `${spreadOf(reaction.id, index)}%` }}
        />
      ))}
    </div>
  );
}

/**
 * OÙ LA BULLE MONTE, entre 4 % et 92 % de la largeur.
 *
 * Un hachage court de l'identifiant, donc stable d'un rendu à l'autre, et assez
 * dispersé pour que deux réactions consécutives ne se superposent pas.
 */
export function spreadOf(id: string, index: number): number {
  let hash = index * 37;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash * 31 + id.charCodeAt(i)) % 8_191;
  }
  return 4 + (hash % 89);
}
